import React, { useEffect, useState, useMemo } from "react";
import { Truck, FileText, Save, Plus } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, orderLabel } from "../lib/constants";
import { useT } from "../lib/i18n";
import { usePrintDoc } from "../lib/usePrintDoc";
import OrderDetailModal from "../components/OrderDetailModal";

export default function Delivery() {
  const toast = useToast();
  const { t } = useT();
  const { printDoc, printing } = usePrintDoc();
  const [orders, setOrders] = useState(null);
  const [adjOrder, setAdjOrder] = useState(null);
  const [adj, setAdj] = useState({});
  const [products, setProducts] = useState([]);
  const [addPid, setAddPid] = useState("");
  const [viewId, setViewId] = useState(null);

  const load = () => api.get("/orders").then(({ data }) =>
    setOrders(data.filter((o) => ["APPROVED", "PURCHASED", "DELIVERY_ADJUSTMENT", "DELIVERED", "RECEIVED"].includes(o.status))));
  useEffect(() => { load(); api.get("/products?active_only=true").then(({ data }) => setProducts(data));
    // eslint-disable-next-line
  }, []);

  const availableProducts = useMemo(
    () => products.filter((p) => !adj[p.id]),
    [products, adj]
  );

  if (!orders) return <PageLoader />;

  const openAdjust = (o) => {
    const a = {};
    o.items.forEach((it) => { a[it.product_id] = { name: it.product_name, unit: it.unit, ordered: it.ordered_quantity, delivered: it.delivered_quantity || it.ordered_quantity }; });
    setAdj(a); setAdjOrder(o);
  };
  const addItem = () => {
    const p = products.find((x) => x.id === addPid);
    if (!p || adj[p.id]) return;
    setAdj((a) => ({ ...a, [p.id]: { name: p.name, unit: p.unit, ordered: 0, delivered: 1, added: true } }));
    setAddPid("");
  };
  const saveAdjust = async () => {
    const items = Object.entries(adj).map(([product_id, v]) => ({ product_id, delivered_quantity: Number(v.delivered) }));
    try {
      await api.post(`/orders/${adjOrder.id}/delivery-adjust`, { items });
      toast.success(t("Delivery confirmed & stock updated"));
      setAdjOrder(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Delivery & Documents")} subtitle={t("Adjust deliveries and generate Surat Jalan / Invoice")} icon={Truck} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {orders.length === 0 && <p className="text-ink-mute">{t("No orders ready for delivery.")}</p>}
        {orders.map((o) => (
          <Card key={o.id} onClick={() => setViewId(o.id)} className="cursor-pointer p-4 transition hover:shadow-cardhover" data-testid={`delivery-${o.order_no}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="truncate font-heading text-base font-extrabold text-ink">{o.branch_name || orderLabel(o)}</p>
                <p className="text-xs text-ink-mute">{o.items.length} {t("items")} · <span className="font-mono">Ref: {o.order_no}</span></p>
              </div>
              <Badge tone={o.status === "DELIVERED" || o.status === "RECEIVED" ? "success" : "warn"}>{t(o.status)}</Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
              {["APPROVED", "PURCHASED", "DELIVERY_ADJUSTMENT"].includes(o.status) && (
                <Button size="sm" onClick={(e) => { e.stopPropagation(); openAdjust(o); }} data-testid={`adjust-${o.order_no}`}>{t("Adjust & Deliver")}</Button>
              )}
              {["DELIVERED", "RECEIVED"].includes(o.status) && (
                <>
                  <Button variant="outline" size="sm" loading={printing === `sj-${o.id}`} onClick={(e) => { e.stopPropagation(); printDoc(`/orders/${o.id}/surat-jalan`, { key: `sj-${o.id}`, label: t("Surat Jalan") }); }} data-testid={`sj-${o.order_no}`}><FileText size={15} /> {t("Print Delivery Note")}</Button>
                  <Button variant="outline" size="sm" loading={printing === `inv-${o.id}`} onClick={(e) => { e.stopPropagation(); printDoc(`/orders/${o.id}/invoice`, { key: `inv-${o.id}`, label: t("Invoice") }); }} data-testid={`inv-${o.order_no}`}><FileText size={15} /> {t("Invoice")}</Button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!adjOrder} onClose={() => setAdjOrder(null)} title={`${t("Delivery Adjustment")} · ${adjOrder?.order_no}`} size="lg"
        footer={<><Button variant="outline" onClick={() => setAdjOrder(null)}>{t("Cancel")}</Button><Button onClick={saveAdjust} data-testid="save-delivery"><Save size={16} /> {t("Confirm Delivery")}</Button></>}>
        <p className="mb-3 text-xs text-ink-soft">{t("Original ordered quantities are preserved. Set the actual delivered quantity.")}</p>
        <div className="space-y-2">
          {Object.entries(adj).map(([pid, v]) => (
            <div key={pid} className="flex items-center gap-3 rounded-xl border border-line px-3 py-2">
              <div className="flex-1">
                <p className="text-sm font-semibold">{v.name} {v.added && <Badge tone="info">{t("added")}</Badge>}</p>
                <p className="text-xs text-ink-mute">{t("Ordered")}: {fmtNum(v.ordered)} {v.unit}</p>
              </div>
              <input type="number" value={v.delivered} onChange={(e) => setAdj((a) => ({ ...a, [pid]: { ...a[pid], delivered: e.target.value } }))}
                className="cf-input w-24 text-right" data-testid={`delivered-${v.name}`} />
              <span className="w-10 text-xs text-ink-mute">{v.unit}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-end gap-2 border-t border-line pt-4">
          <div className="flex-1">
            <label className="cf-label">{t("Add additional item")}</label>
            <select value={addPid} onChange={(e) => setAddPid(e.target.value)} className="cf-input" data-testid="add-item-select">
              <option value="">{t("Select product...")}</option>
              {availableProducts.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
            </select>
          </div>
          <Button variant="secondary" onClick={addItem} data-testid="add-extra-item"><Plus size={16} /> {t("Add")}</Button>
        </div>
      </Modal>

      <OrderDetailModal orderId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
